/**
 * overlayRegistry (T27.2.7.3) — a module-level LIFO stack of open overlays' close
 * handlers, so one app-level hardware-Back handler (useAndroidBackButton) can ask
 * "is anything open?" and "close the topmost one".
 *
 * Why a registry, not React state:
 *   Every overlay (deal sheet, account menu, capture sheet, settings modals)
 *   holds its open-state in its own React tree with no link to navigation. The
 *   Android hardware Back button fires through Capacitor, outside React's event
 *   system; it has no way to know an overlay is open, so it falls through to the
 *   default `history.back()` and pops the route UNDER the overlay (or exits the
 *   app). A single shared registry gives the Back handler one place to look.
 *
 * Shape:
 *   - `registerOverlay(close)` pushes a close handler onto the stack and returns
 *     a stable `deregister()` that removes THIS entry (by identity, not position,
 *     so out-of-order close still removes the right one).
 *   - `closeTopmost()` pops + invokes the most recently registered handler,
 *     returning true if one was closed (LIFO — the thing visually on top).
 *   - `hasOpenOverlay()` reports whether anything is registered.
 *
 * The registry only tracks "how to close" — it never renders, locks scroll, or
 * touches the DOM. Overlays keep owning their own open-state, animation, and
 * Escape/backdrop close paths; they just also register here while open so Back
 * can reach them. Pure + framework-free, so it unit-tests without React.
 */

/** A function that closes one overlay (the same callback its Escape/backdrop path runs). */
export type OverlayCloseHandler = () => void;

interface OverlayEntry {
  close: OverlayCloseHandler;
}

// The live stack. Index 0 is the oldest open overlay; the last element is the
// topmost (most recently opened) — the one Back closes first.
const stack: OverlayEntry[] = [];

/**
 * Register an open overlay's close handler. Call when an overlay opens; call the
 * returned `deregister` when it closes (or on unmount). Deregistration is by
 * entry identity, so an overlay that closes out of LIFO order (e.g. via its own
 * backdrop tap) still removes exactly its own entry.
 */
export function registerOverlay(close: OverlayCloseHandler): () => void {
  const entry: OverlayEntry = { close };
  stack.push(entry);
  return () => {
    const index = stack.indexOf(entry);
    if (index !== -1) stack.splice(index, 1);
  };
}

/**
 * Close the topmost (most recently registered) overlay. Returns true if one was
 * closed, false if nothing was open. The entry is removed before its handler
 * runs so a handler that itself triggers cleanup can't double-pop.
 */
export function closeTopmost(): boolean {
  const entry = stack.pop();
  if (!entry) return false;
  entry.close();
  return true;
}

/** True while at least one overlay is registered as open. */
export function hasOpenOverlay(): boolean {
  return stack.length > 0;
}

/** Current number of open overlays (LIFO depth). Exposed for tests + diagnostics. */
export function openOverlayCount(): number {
  return stack.length;
}

/**
 * Test-only reset of the module-level stack. Vitest shares module state across
 * tests in a file; a test asserting "nothing open" must start clean. Not exported
 * from the app barrel.
 */
export function __resetOverlayRegistryForTest(): void {
  stack.length = 0;
}
